function getApiOrigin(): string {
  try {
    const envUrl = (import.meta as any)?.env?.VITE_API_URL as string | undefined;
    if (envUrl && envUrl.trim().length > 0) {
      if (/^https?:\/\//i.test(envUrl)) {
        const looksLocal = /localhost|127\.0\.0\.1/i.test(envUrl);
        if (typeof window !== 'undefined' && looksLocal) {
          const pageOrigin = (window as any)?.location?.origin || '';
          if (pageOrigin) return `${pageOrigin}/api`;
        }
        try { return new URL(envUrl).origin; } catch { return envUrl; }
      }
    }
    const meta = typeof document !== 'undefined' ? (document.querySelector('meta[name="api-base-url"]') as HTMLMetaElement | null) : null;
    const metaUrl = meta?.content && meta.content.trim().length > 0 ? meta.content.trim() : undefined;
    const winUrl = typeof window !== 'undefined' ? (window as any).__MEGAJOB_API_URL__ : undefined;
    const runtimeUrl = (metaUrl && metaUrl.length > 0) ? metaUrl : (typeof winUrl === 'string' ? winUrl : undefined);
    if (runtimeUrl && /^https?:\/\//i.test(runtimeUrl)) {
      try { return new URL(runtimeUrl).origin; } catch { return runtimeUrl; }
    }
  } catch { }
  try {
    const isBrowser = typeof window !== 'undefined' && !!(window as any)?.location?.origin;
    const origin = isBrowser ? (window as any).location.origin : '';
    if (origin) {
      // Prefer same-origin API to avoid ORB in dev and prod
      return `${origin}/api`;
    }
  } catch { }
  return '';
}

export function normalizeMediaUrl(url?: string): string | undefined {
  if (!url || typeof url !== 'string') return url;
  try {
    // Normalize any '/api/uploads' prefix to '/uploads' for consistent handling
    if (url.startsWith('/api/uploads/')) {
      url = url.replace('/api/uploads/', '/uploads/');
    }

    const uploadsIndex = url.indexOf('/uploads/');

    // Absolute URLs: if they reference uploads path, rewrite to API origin
    if (/^https?:\/\//i.test(url)) {
      try {
        const u = new URL(url);
        if (uploadsIndex !== -1) {
          let path = url.substring(uploadsIndex);

          if (/^\/uploads\/profile-images\/member_/i.test(path)) {
            path = path.replace('/uploads/profile-images/', '/uploads/team-member-images/');
          }
          if (/^\/uploads\/hero-images\//i.test(path)) {
            path = path.replace('/uploads/hero-images/', '/uploads/job-cover-images/');
          }
          if (/^\/uploads\/job-covers\//i.test(path)) {
            path = path.replace('/uploads/job-covers/', '/uploads/job-cover-images/');
          }
          // Keep employer logos and jobseeker images as-is
          // No rewrite for '/uploads/employer-logos/*' or '/uploads/jobseeker-images/*'
          if (/^\/uploads\/company-logos\//i.test(path) && !/\/uploads\/company-logos\/company_/i.test(path)) {
            const seg = path.replace('/uploads/company-logos/', '');
            path = `/uploads/company-logos/company_${seg}`;
          }

          const apiOrigin = getApiOrigin();
          const isBrowser = typeof window !== 'undefined' && !!(window as any)?.location?.origin;
          const pageOrigin = isBrowser ? (window as any).location.origin : '';
          const fallbackOrigin = pageOrigin ? `${pageOrigin}/api` : '';
          let originToUse = apiOrigin && !apiOrigin.startsWith('/') ? apiOrigin : fallbackOrigin;

          if (originToUse.endsWith('/api') && path.startsWith('/uploads/')) {
            originToUse = originToUse.slice(0, -4);
          }

          if (originToUse) {
            return `${originToUse}${path.startsWith('/') ? '' : '/'}${path}`;
          }
          return path; // last resort
        }
        if (/localhost:3005\//i.test(url) && uploadsIndex === -1) {
          const apiOrigin = getApiOrigin();
          const isBrowser = typeof window !== 'undefined' && !!(window as any)?.location?.origin;
          const pageOrigin = isBrowser ? (window as any).location.origin : '';
          const originToUse = apiOrigin && !apiOrigin.startsWith('/') ? apiOrigin : (pageOrigin ? `${pageOrigin}/api` : '');
          if (originToUse) {
            return url.replace(/https?:\/\/localhost:3005\//i, `${originToUse}/`);
          }
        }
        return url; // Non-uploads absolute URLs (e.g., Cloudinary)
      } catch {
        // Fall through
      }
    }

    // Relative '/uploads' paths: prefer API origin
    if (uploadsIndex !== -1) {
      let path = url.substring(uploadsIndex);

      if (/^\/uploads\/profile-images\/member_/i.test(path)) {
        path = path.replace('/uploads/profile-images/', '/uploads/team-member-images/');
      }
      if (/^\/uploads\/hero-images\//i.test(path)) {
        path = path.replace('/uploads/hero-images/', '/uploads/job-cover-images/');
      }
      if (/^\/uploads\/job-covers\//i.test(path)) {
        path = path.replace('/uploads/job-covers/', '/uploads/job-cover-images/');
      }
      // Keep employer logos and jobseeker images as-is
      // No rewrite for '/uploads/employer-logos/*' or '/uploads/jobseeker-images/*'
      if (/^\/uploads\/company-logos\//i.test(path) && !/\/uploads\/company-logos\/company_/i.test(path)) {
        const seg = path.replace('/uploads/company-logos/', '');
        path = `/uploads/company-logos/company_${seg}`;
      }

      const apiOrigin = getApiOrigin();
      const isBrowser = typeof window !== 'undefined' && !!(window as any)?.location?.origin;
      const pageOrigin = isBrowser ? (window as any).location.origin : '';
      const fallbackOrigin = pageOrigin ? `${pageOrigin}/api` : '';
      let originToUse = apiOrigin && !apiOrigin.startsWith('/') ? apiOrigin : fallbackOrigin;

      // If we are serving uploads, they are typically at the root (proxied in dev), so we don't need the /api prefix
      if (path.startsWith('/uploads/') || path.startsWith('uploads/')) {
        if (originToUse.endsWith('/api')) {
          originToUse = originToUse.slice(0, -4);
        }
      }

      if (originToUse) {
        return `${originToUse}${path.startsWith('/') ? '' : '/'}${path}`;
      }
      return path.startsWith('/') ? path : `/${path}`;
    }

    return url;
  } catch {
    return url;
  }
}

function initialsFromName(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  const first = parts[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1] : '';
  const i1 = first.charAt(0).toUpperCase();
  const i2 = last.charAt(0).toUpperCase();
  return (i1 + i2) || i1 || 'C';
}

function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function paletteColor(name: string): string {
  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#22C55E', '#E11D48'];
  const idx = hashString(name) % colors.length;
  return colors[idx];
}

export function makeInitialsAvatar(name: string, size: number = 128, bg?: string, color: string = '#FFFFFF'): string {
  const initials = initialsFromName(name);
  const background = bg && bg !== 'random' ? bg : paletteColor(name);
  const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><rect width="${size}" height="${size}" rx="${Math.floor(size / 6)}" fill="${background}"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Inter,Arial,sans-serif" font-weight="700" font-size="${Math.floor(size * 0.45)}" fill="${color}">${initials}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function getAvatarUrl(name: string, size: number = 128): string {
  try {
    const base = (import.meta as any)?.env?.VITE_AVATAR_BASE_URL as string | undefined;
    if (base && /^https?:\/\//i.test(base)) {
      const params = new URLSearchParams({ name: String(name || ''), size: String(size) });
      return `${base}?${params.toString()}`;
    }
  } catch { }
  return makeInitialsAvatar(name, size);
}
